import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '../store/useAuthStore';
import { useGameStore } from '../store/useGameStore';
import { useTurnAlertStore } from '../store/useTurnAlertStore';
import { getHumanTurnToken, isHumanTurnGame, playTurnNotificationSound } from '../utils/turnAlerts';
import { showSystemNotification } from '../utils/systemNotifications';
import { PUSH_NOTIFICATIONS_ENABLED } from '../config/pushNotifications';

const storageKeyForUser = (uid: string) => `verbo_seen_human_turns_${uid}`;

const readSeenTurns = (uid: string): Record<string, string> => {
  try {
    const raw = localStorage.getItem(storageKeyForUser(uid));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeSeenTurns = (uid: string, value: Record<string, string>) => {
  localStorage.setItem(storageKeyForUser(uid), JSON.stringify(value));
};

export function useHumanTurnAlerts() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const activeGames = useGameStore((state) => state.activeGames);
  const activeGamesLoaded = useGameStore((state) => state.activeGamesLoaded);
  const activeGamesUserId = useGameStore((state) => state.activeGamesUserId);
  const setPendingHumanTurns = useTurnAlertStore((state) => state.setPendingHumanTurns);
  const previousPendingTokensRef = useRef<Set<string>>(new Set());
  const hasBootstrappedRef = useRef(false);

  const humanTurnGames = useMemo(() => {
    if (!user?.uid) return [];
    return activeGames.filter((game) => isHumanTurnGame(game, user.uid));
  }, [activeGames, user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setPendingHumanTurns([]);
      hasBootstrappedRef.current = false;
      previousPendingTokensRef.current = new Set();
      return;
    }

    const currentGameMatch = pathname.match(/^\/game\/([^/]+)/);
    if (!currentGameMatch) return;

    const currentGameId = currentGameMatch[1];
    const currentGame = humanTurnGames.find((game) => game.id === currentGameId);
    if (!currentGame) return;

    const seenTurns = readSeenTurns(user.uid);
    const token = getHumanTurnToken(currentGame);
    if (seenTurns[currentGame.id] === token) return;

    writeSeenTurns(user.uid, { ...seenTurns, [currentGame.id]: token });
  }, [humanTurnGames, pathname, user?.uid]);

  useEffect(() => {
    if (!user?.uid || !activeGamesLoaded || activeGamesUserId !== user.uid) return;

    const seenTurns = readSeenTurns(user.uid);
    const pendingGames = humanTurnGames.filter((game) => seenTurns[game.id] !== getHumanTurnToken(game));
    const pendingTokens = new Set(pendingGames.map(getHumanTurnToken));

    setPendingHumanTurns(pendingGames.map((game) => game.id));

    if (!hasBootstrappedRef.current) {
      hasBootstrappedRef.current = true;
      previousPendingTokensRef.current = pendingTokens;
      return;
    }

    const newPendingGame = pendingGames.find((game) => !previousPendingTokensRef.current.has(getHumanTurnToken(game)));
    if (newPendingGame) {
      const gameUrl = `/game/${newPendingGame.id}`;
      playTurnNotificationSound();
      if (PUSH_NOTIFICATIONS_ENABLED) {
        showSystemNotification({
          kind: 'turn',
          title: 'Nueva jugada',
          body: 'Tienes una nueva jugada esperándote.',
          url: gameUrl,
          tag: `verbo-turn-${newPendingGame.id}`,
        }).catch(() => {
          // The in-app toast remains the fallback if the platform blocks system notifications.
        });
      }
      toast.success('Nueva jugada', {
        description: 'Tienes una nueva jugada esperándote.',
        duration: 7000,
        action: {
          label: 'Ver partida',
          onClick: () => navigate(gameUrl),
        },
      });
    }

    previousPendingTokensRef.current = pendingTokens;
  }, [activeGamesLoaded, activeGamesUserId, humanTurnGames, navigate, pathname, setPendingHumanTurns, user?.uid]);
}
